"use client";

import { useState, useRef } from "react";
import Image from "next/image";
import { motion, AnimatePresence } from "framer-motion";
import { X, ZoomIn, ChevronLeft, ChevronRight } from "lucide-react";
import { FALLBACK_IMAGE } from "@/lib/shared/utils";

interface ProductGalleryProps {
  images: string[];
  productName: string;
}

export default function ProductGallery({ images, productName }: ProductGalleryProps) {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [activeScrollIndex, setActiveScrollIndex] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  if (!images || images.length === 0) return null;

  const openLightbox = (index: number) => setSelectedIndex(index);
  const closeLightbox = () => setSelectedIndex(null);

  const nextImage = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setSelectedIndex((prev) => (prev === null ? null : prev === images.length - 1 ? 0 : prev + 1));
  };

  const prevImage = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setSelectedIndex((prev) => (prev === null ? null : prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, clientWidth } = scrollRef.current;
      const index = Math.round(scrollLeft / clientWidth);
      setActiveScrollIndex(index);
    }
  };

  const scrollToIndex = (index: number) => {
    if (scrollRef.current) {
      const width = scrollRef.current.clientWidth;
      scrollRef.current.scrollTo({ left: width * index, behavior: "smooth" });
    }
  };

  const swipeConfidenceThreshold = 10000;
  const swipePower = (offset: number, velocity: number) => {
    return Math.abs(offset) * velocity;
  };

  return (
    <div className="relative w-full space-y-4 md:space-y-0">
      {/* Gallery Container */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="hide-scrollbar flex h-fit w-full snap-x snap-mandatory overflow-x-auto md:grid md:grid-cols-2 md:gap-4 md:overflow-visible"
      >
        {images.map((image, idx) => (
          <div
            key={idx}
            role="button" tabIndex={0} onKeyDown={(e)=>{if(e.key==="Enter"||e.key===" "){e.preventDefault(); (e.currentTarget as HTMLElement).click()}}} onClick={() => openLightbox(idx)}
            className="group bg-muted dark:bg-muted/50 relative flex aspect-[3/4] w-full min-w-full flex-shrink-0 cursor-zoom-in snap-center items-center justify-center overflow-hidden p-4 md:min-w-0"
          >
            <div className="relative h-full w-full">
              <Image
                src={image}
                alt={`${productName} view ${idx + 1}`}
                fill
                className="object-contain transition-opacity duration-300 group-hover:opacity-80"
                sizes="(max-width: 768px) 100vw, 50vw"
                priority={idx < 2}
              />
            </div>
            {/* Overlay hint */}
            <div className="text-foreground absolute right-4 bottom-4 p-2 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
              <ZoomIn className="h-4 w-4" />
            </div>
          </div>
        ))}
      </div>

      {/* Mobile Thumbnail Strip */}
      <div className="hide-scrollbar flex w-full gap-3 overflow-x-auto px-4 pb-2 md:hidden">
        {images.map((image, idx) => (
          <button
            key={idx}
            onClick={() => scrollToIndex(idx)}
            aria-label={`View image ${idx + 1}`}
            className={`bg-muted dark:bg-muted relative h-20 w-16 flex-shrink-0 overflow-hidden transition-opacity duration-200 ${
              activeScrollIndex === idx
                ? "border-foreground dark:border-background border-b-2 opacity-100"
                : "border-b-2 border-transparent opacity-70"
            }`}
          >
            <Image
              src={image}
              alt={`Thumbnail ${idx + 1}`}
              fill
              className="object-contain p-1"
              sizes="64px"
            />
          </button>
        ))}
      </div>

      {/* Lightbox Modal */}
      <AnimatePresence>
        {selectedIndex !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="bg-background/95 fixed inset-0 z-50 flex items-center justify-center backdrop-blur-md"
            onClick={closeLightbox}
          >
            {/* Close Button */}
            <button
              onClick={closeLightbox}
              aria-label="Close lightbox"
              className="hover:bg-secondary/20 absolute top-4 right-4 z-50 cursor-pointer p-2 transition-colors"
            >
              <X className="text-foreground h-8 w-8" />
            </button>

            {/* Navigation Buttons */}
            <button
              onClick={prevImage}
              aria-label="Previous image"
              className="hover:bg-secondary/20 absolute left-2 z-50 cursor-pointer p-3 transition-colors md:left-4"
            >
              <ChevronLeft className="h-8 w-8" />
            </button>
            <button
              onClick={nextImage}
              aria-label="Next image"
              className="hover:bg-secondary/20 absolute right-2 z-50 cursor-pointer p-3 transition-colors md:right-4"
            >
              <ChevronRight className="h-8 w-8" />
            </button>

            {/* Main Image Container */}
            <motion.div
              key={selectedIndex}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={1}
              onDragEnd={(e, { offset, velocity }) => {
                const swipe = swipePower(offset.x, velocity.x);

                if (swipe < -swipeConfidenceThreshold) {
                  nextImage();
                } else if (swipe > swipeConfidenceThreshold) {
                  prevImage();
                }
              }}
              className="relative flex h-full max-h-screen w-full max-w-7xl items-center justify-center p-4 md:p-10"
              onClick={(e) => e.stopPropagation()} // Prevent close when clicking image area
            >
              <div className="pointer-events-none relative h-full w-full select-none">
                <Image
                  src={images[selectedIndex] || FALLBACK_IMAGE}
                  alt={`${productName} fullscreen view ${selectedIndex + 1}`}
                  fill
                  sizes="100vw"
                  className="object-contain"
                  quality={100}
                  priority
                />
              </div>
            </motion.div>

            {/* Counter */}
            <div className="text-muted-foreground absolute bottom-6 left-1/2 -translate-x-1/2 text-sm font-medium tracking-widest uppercase">
              {selectedIndex + 1} / {images.length}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
